import { css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-query';

import { Spacing } from 'chatie-design-system';
import { Textarea } from 'chatie-design-system/form';

import starActiveImg from '../../public/images/appReview/star_active.png';
import starInactiveImg from '../../public/images/appReview/star_inactive.png';

import { PostReasonsPayload, postReasons } from '../../api/common';
import { UniversalReason } from '../../constants';
import { marginXTop } from '../../styles/css';
import { useAnalytics } from '../../utils/analytics';
import { useForm } from '../../utils/form';
import { ParsedStorage } from '../../utils/localStorage';
import { isEnv } from '../../utils/misc';
import { useWebView } from '../../utils/webview';
import Image from '../common/Image';
import Modal, { ModalButton, ModalProps } from '../common/Modal';

const ratingDescriptionCSS = css`
  text-align: center;
`;

const ratingCSS = css`
  display: flex;
  justify-content: center;
  margin-top: ${Spacing.medium}px;

  button + button {
    margin-left: ${Spacing.xsmall}px;
  }
`;

const AppReviewModal = (props: ModalProps) => {
  const { onClose } = props;
  const { t } = useTranslation();

  const { web, listen } = useWebView();
  const { firebase } = useAnalytics();

  const [view, setView] = useState<'rating' | 'feedback'>('rating');

  const { mutate: submitFeedback } = useMutation(
    (payload: PostReasonsPayload) =>
      postReasons(UniversalReason.appReview, payload)
  );

  const { values, onChange, setValue, isSubmitting, isValidated, submit } =
    useForm(
      {
        rating: 0,
        reason: '',
      },
      {
        validator: (values) => {
          const reason = values.reason.trim();
          if (!reason) {
            return [false, t('내용을 입력해주세요.')];
          }

          return [true, undefined];
        },
        onSubmit: (done) => {
          submitFeedback(values, {
            onSuccess: () => {
              alert(t('소중한 의견 감사합니다!'));
              onClose();
            },
            onSettled: done,
          });
        },
      }
    );

  const handleRating = useCallback(() => {
    if (values.rating === 0) {
      alert(t('평가를 입력해주세요.'));
      return;
    }

    if (values.rating > 3) {
      web.showAppReview();
    } else {
      setView('feedback');
    }
  }, [values.rating]);

  const review = useCallback((answer: 'yes' | 'no' | 'later' | 'far_later') => {
    const day = 1000 * 60 * 60 * 24;
    const minute = 1000 * 60;
    const now = new Date().getTime();
    const isProduction = isEnv('production');

    switch (answer) {
      case 'yes': {
        const disableDays = isProduction ? day * 365 : minute * 60;
        ParsedStorage.setItem('hideAppReviewUntil', now + disableDays);
        break;
      }

      case 'no': {
        const disableDays = isProduction ? day * 15 : minute * 15;
        ParsedStorage.setItem('hideAppReviewUntil', now + disableDays);
        break;
      }

      case 'later': {
        const disableDays = isProduction ? day * 7 : minute * 3;
        ParsedStorage.setItem('hideAppReviewUntil', now + disableDays);
        break;
      }

      case 'far_later': {
        const disableDays = isProduction ? day * 90 : minute * 5;
        ParsedStorage.setItem('hideAppReviewUntil', now + disableDays);
      }

      default:
        break;
    }

    firebase.logEvent('app_review', { answer });

    onClose();
  }, []);

  // 앱 리뷰 리스너
  useEffect(() => {
    const unlisten = listen('induceAppReview', (e) => {
      const { value } = e.detail;

      if (value === 0) {
        review('no');
      } else if (value === 1) {
        review('later');
      } else {
        review('yes');
      }
    });

    return unlisten;
  }, []);

  return (
    <Modal {...props} title={t('채티 만족도 조사')} hideCloseButton>
      {view === 'rating' && (
        <>
          <h5 css={ratingDescriptionCSS}>
            {t('채티 사용에 만족하고 있으신가요?')}
          </h5>
          <div css={ratingCSS}>
            {[...Array(5).keys()].map((i) => (
              <button
                key={`app-review-rating-${i}`}
                onClick={() => setValue('rating', i + 1)}
              >
                <Image
                  src={values.rating > i ? starActiveImg : starInactiveImg}
                  width={40}
                  height={38}
                  alt={t('별점')}
                />
              </button>
            ))}
          </div>

          <ModalButton text={t('평가하기')} onClick={handleRating} />
        </>
      )}
      {view === 'feedback' && (
        <>
          <h5 className="multiline">
            {t('소중한 의견 참고하여\n더 좋은 채티를 만들어가겠습니다!')}
          </h5>
          <Textarea
            css={marginXTop(Spacing.medium)}
            placeholder={t('소중한 의견을 적어주세요')}
            minRows={4}
            maxRows={4}
            maxLength={300}
            name="reason"
            value={values.reason}
            onChange={onChange}
          />

          <ModalButton
            text={t('전송하기')}
            disabled={!isValidated || isSubmitting}
            onClick={submit}
          />
          <ModalButton
            type="text"
            text={t('다음에 할게요')}
            onClick={() => {
              review('far_later');
            }}
          />
        </>
      )}
    </Modal>
  );
};

export default AppReviewModal;
